import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CircleCheck, Loader2, CircleX } from "lucide-react";
import Header from "@/components/Header";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MAX_POLLS = 15;

export default function PaymentResult({ cancelled = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const jobId = params.get("job_id");
  const { refreshUser } = useAuth();
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const polls = useRef(0);

  useEffect(() => {
    if (cancelled || !sessionId) {
      if (!cancelled) setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setState("paid");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState("error");
          return;
        }
      } catch (_) { /* retry */ }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) setState("error");
      else timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [cancelled, sessionId, refreshUser]);

  const view = {
    checking: { icon: <Loader2 className="w-8 h-8 animate-spin text-[#ED5A24]" />, title: "Confirming your payment…", body: "This only takes a few seconds." },
    paid: { icon: <CircleCheck className="w-8 h-8 text-[#16A34A]" />, title: "Your logo is unlocked.", body: "All files, the brand guide, social kit and stickers for this logo are ready to download." },
    cancelled: { icon: <CircleX className="w-8 h-8 text-[#52525B]" />, title: "Payment cancelled", body: "No charge was made. You can unlock this logo any time from your dashboard." },
    error: { icon: <CircleX className="w-8 h-8 text-[#DC2626]" />, title: "We couldn't confirm the payment", body: "If you were charged, refresh this page in a minute or contact us." },
  }[state];

  return (
    <div className="min-h-screen bg-white" data-testid="payment-result-page">
      <Header />
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid={`payment-state-${state}`}>
        <div className="flex justify-center mb-6">{view.icon}</div>
        <h1 className="text-3xl sm:text-4xl tracking-tighter font-black mb-3">{view.title}</h1>
        <p className="text-[#52525B] mb-10">{view.body}</p>
        {state !== "checking" && (
          <Link
            to={jobId ? `/dashboard?job=${jobId}` : "/dashboard"}
            data-testid="payment-go-dashboard"
            className="inline-flex items-center bg-[#0A0A0B] hover:bg-[#ED5A24] text-white text-xs uppercase tracking-[0.2em] font-bold h-12 px-8 transition-colors duration-200"
          >
            {jobId ? "Back to my logo" : "Go to dashboard"}
          </Link>
        )}
      </div>
    </div>
  );
}
